import React, { createContext, useContext } from 'react';
import { ASLEEP_COPY } from '../../utils/aiAsleep';

/**
 * 🌙 The asleep look (W2b, treatment C, Nina 09-26; the words and rules live
 * in utils/aiAsleep.js; mockup https://claude.ai/artifact/S8akGF3U7xM1zRBgSMmd5n).
 *   AiPowerCard    the asleep / wake card in the feed (GameAnnouncementAction
 *                  routes `data.ai_power` here first, on every form factor)
 *   RestingBanner  the line above the message box (HumanInputForm's `resting`)
 *   AsleepReceipt  "💤 Saved for when they wake" under the viewer's newest
 *                  note; AsleepContext (provided once by GameStepPage) names
 *                  which, so no prop threads through the memoized feed
 * The night palette is the mockup's moon blue. A support link always sits with
 * its note (Nina 09-26: the money only runs the house, it pays nobody).
 * ⚠️ This frontend has NO global box-sizing: every padded box sets it.
 */
const MOON = '#c2caf4';
const NIGHT_BG = 'rgba(80,90,140,0.16)';
const NIGHT_LINE = 'rgba(174,182,224,0.4)';

export const AsleepContext = createContext({ receiptFor: null });

/** A Play button while everyone sleeps: calm moon glass, no glow (spread LAST over its style). */
export const ASLEEP_PLAY = {
  backgroundColor: 'rgba(80,90,140,0.18)', border: '1px solid rgba(174,182,224,0.35)', color: '#d6dbf5',
  cursor: 'default', opacity: 1, textShadow: 'none', boxShadow: 'none', animation: 'none',
};

function SupportLine({ support, pill = false }) {
  if (!support) return null;
  return (
    <span data-asleep-support="1" style={{ display: 'grid', gap: 4, justifyItems: pill ? 'center' : 'start', flexBasis: '100%' }}>
      <a href={support.url} target="_blank" rel="noopener noreferrer" style={pill ? {
        fontWeight: 700, fontSize: '0.85em', color: '#2a2520', background: '#e9b565',
        borderRadius: 999, padding: '6px 13px', textDecoration: 'none',
      } : { fontWeight: 700, color: '#e9b565', textDecoration: 'none' }}>
        {support.label}{pill ? '' : ' →'}
      </a>
      <span style={{ fontSize: '0.8em', opacity: 0.8 }}>{support.note}</span>
    </span>
  );
}

export function AiPowerCard({ card, time = null, theme = {} }) {
  if (!card) return null;
  const night = card.kind === 'asleep';
  return (
    <div data-power-card={card.kind} role="status" style={{
      boxSizing: 'border-box', width: 'calc(100% - 16px)', maxWidth: 460, margin: '10px auto',
      display: 'grid', justifyItems: 'center', gap: 7, padding: '10px 14px', textAlign: 'center',
      borderRadius: 12, lineHeight: 1.4,
      border: `1px solid ${night ? NIGHT_LINE : 'rgba(196,149,106,0.34)'}`,
      background: night ? NIGHT_BG : 'rgba(240,230,217,0.05)',
      color: theme.textDark || '#f0e6d9',
    }}>
      <span aria-hidden="true" style={{ fontSize: 18, lineHeight: 1 }}>{card.emoji}</span>
      <span style={{ fontSize: '0.92em' }}>{card.text}</span>
      <SupportLine support={card.support} pill />
      {time && <span style={{ fontSize: '0.68em', color: theme.textMuted || '#a89b8c' }}>{time}</span>}
    </div>
  );
}

export function RestingBanner({ resting }) {
  if (!resting || !resting.text) return null;
  return (
    <div data-resting-banner="1" role="status" style={{
      boxSizing: 'border-box', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 8px', minWidth: 0,
      margin: '0 0 6px', padding: '5px 9px', borderRadius: 8,
      border: '1px solid rgba(174,182,224,0.3)', background: 'rgba(80,90,140,0.22)',
      color: '#d6dbf5', fontSize: '0.8em', lineHeight: 1.3,
    }}>
      <span aria-hidden="true" style={{ flexShrink: 0 }}>💤</span>
      <span style={{ flex: '1 1 0', minWidth: 0 }}>{resting.text}</span>
      <SupportLine support={resting.support} />
    </div>
  );
}

export function AsleepReceipt({ actionId }) {
  const { receiptFor } = useContext(AsleepContext);
  if (!receiptFor || !actionId || receiptFor !== actionId) return null;
  return (
    <div data-asleep-receipt="1" role="status" style={{ alignSelf: 'flex-end', margin: '1px 6px 2px 0', fontSize: '0.72em', color: MOON }}>
      {ASLEEP_COPY.receipt}
    </div>
  );
}
